/**
 * ShareMeetingModal - Share meeting with contacts or externally
 *
 * Features:
 * - Display meeting access code
 * - Share to contacts (sends DM + push notification)
 * - External share options (copy link, email, etc.)
 * - Personal note field for invitations
 *
 * @see DESIGN_SYSTEM.md for UI guidelines
 */

import { useState, useEffect, useMemo } from 'react';
import { useContacts } from '../context/ContactContext';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { findExistingDM, createDirectMessageSecure, sendMeetingInviteMessage } from '../services/chatService';
import { sendMeetingInvite } from '../services/meetingInviteService';
import { playSound, SOUNDS } from '../utils/soundEffects';

export default function ShareMeetingModal({
  isOpen,
  onClose,
  meetingId,
  meetingName,
  accessCode
}) {
  const { user, userProfile } = useAuth();
  const { contacts } = useContacts();
  const toast = useToast();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [personalNote, setPersonalNote] = useState('');
  const [copied, setCopied] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [activeTab, setActiveTab] = useState('contacts'); // 'contacts' or 'external'

  const shareLink = `${window.location.origin}/meet/${meetingId}`;

  // Filter contacts based on search query
  const filteredContacts = useMemo(() => {
    if (!searchQuery.trim()) return contacts;
    const query = searchQuery.toLowerCase();
    return contacts.filter(contact =>
      contact.contactName?.toLowerCase().includes(query) ||
      contact.contactEmail?.toLowerCase().includes(query) ||
      contact.nickname?.toLowerCase().includes(query)
    );
  }, [contacts, searchQuery]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setSelectedContacts([]);
      setPersonalNote('');
      setActiveTab('contacts');
    }
  }, [isOpen]);

  const toggleContactSelection = (contact) => {
    setSelectedContacts(prev => {
      const isSelected = prev.some(c => c.contactId === contact.contactId);
      if (isSelected) {
        return prev.filter(c => c.contactId !== contact.contactId);
      }
      return [...prev, contact];
    });
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      playSound(SOUNDS.INVITE_SENT);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      toast.error('Failed to copy link');
    }
  };

  const shareViaEmail = () => {
    const subject = encodeURIComponent(`Join my meeting${meetingName ? `: ${meetingName}` : ''}`);
    const body = encodeURIComponent(
      `${personalNote ? personalNote + '\n\n' : ''}Join my meeting:\n\n${shareLink}${accessCode ? `\n\nAccess code: ${accessCode}` : ''}`
    );
    playSound(SOUNDS.INVITE_SENT);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const shareViaWhatsApp = () => {
    const text = encodeURIComponent(
      `${personalNote ? personalNote + '\n\n' : ''}Join my meeting: ${shareLink}${accessCode ? `\n\nAccess code: ${accessCode}` : ''}`
    );
    playSound(SOUNDS.INVITE_SENT);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: meetingName || 'Join my meeting',
          text: personalNote || 'Join me for a meeting',
          url: shareLink
        });
        playSound(SOUNDS.INVITE_SENT);
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Error sharing:', err);
        }
      }
    }
  };

  const sendInvitesToContacts = async () => {
    if (selectedContacts.length === 0) {
      toast.error('Select at least one contact');
      return;
    }

    setIsSending(true);

    try {
      const senderName = userProfile?.displayName || user?.displayName || user?.email?.split('@')[0] || 'Someone';
      const senderPhoto = userProfile?.photoURL || user?.photoURL || null;

      let successCount = 0;
      let failCount = 0;

      for (const contact of selectedContacts) {
        try {
          // Step 1: Create meeting invite document (triggers push notification)
          await sendMeetingInvite({
            fromUserId: user.uid,
            toUserId: contact.contactId,
            fromUserName: senderName,
            meetingId,
            meetingName,
            accessCode,
            personalNote
          });

          // Step 2: Try to send chat message (best effort - don't fail if this doesn't work)
          try {
            let chat = await findExistingDM(user.uid, contact.contactId);

            if (!chat) {
              // Try to create DM via Cloud Function
              chat = await createDirectMessageSecure(
                user.uid,
                contact.contactId,
                senderName,
                contact.contactName,
                senderPhoto,
                contact.contactPhotoURL
              );
            }

            if (chat?.id) {
              await sendMeetingInviteMessage(
                chat.id,
                user.uid,
                senderName,
                senderPhoto,
                {
                  meetingId,
                  meetingName,
                  accessCode,
                  personalNote
                }
              );
            }
          } catch (chatErr) {
            // Chat message failed but invite was sent - that's OK
            console.warn('Chat message failed, but invite was sent:', chatErr.message);
          }

          successCount++;
        } catch (err) {
          console.error(`Failed to send invite to ${contact.contactName}:`, err.message);
          failCount++;
        }
      }

      if (successCount > 0) {
        playSound(SOUNDS.INVITE_SENT);
        toast.success(`Invite sent to ${successCount} ${successCount === 1 ? 'contact' : 'contacts'}`);
        onClose();
      }

      if (failCount > 0) {
        toast.error(`Failed to send ${failCount} ${failCount === 1 ? 'invite' : 'invites'}`);
      }
    } catch (err) {
      console.error('Failed to send invites:', err);
      toast.error('Failed to send invites');
    } finally {
      setIsSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-[#1c1c1e] rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden border border-[#d2d2d7] dark:border-[#2c2c2e]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-[#f5f5f7] dark:bg-[#2c2c2e] border-b border-[#d2d2d7] dark:border-[#3c3c3e] px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-[#1d1d1f] dark:text-white">
            Share Meeting
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-[#e8e8ed] dark:hover:bg-[#3c3c3e] rounded-lg transition"
          >
            <svg className="w-5 h-5 text-[#86868b]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Access Code Display */}
          {accessCode && (
            <div className="bg-[#f5f5f7] dark:bg-[#2c2c2e] rounded-xl p-4">
              <label className="block text-sm font-medium text-[#86868b] dark:text-[#8e8e93] mb-2">
                Access Code
              </label>
              <div className="flex items-center justify-between">
                <span className="text-2xl font-mono font-bold text-[#1d1d1f] dark:text-white tracking-widest">
                  {accessCode}
                </span>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(accessCode);
                    toast.success('Access code copied');
                    playSound(SOUNDS.INVITE_SENT);
                  }}
                  className="p-2 hover:bg-[#e8e8ed] dark:hover:bg-[#3c3c3e] rounded-lg transition"
                  title="Copy access code"
                >
                  <svg className="w-5 h-5 text-[#06c]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {/* Meeting Link */}
          <div>
            <label className="block text-sm font-medium text-[#86868b] dark:text-[#8e8e93] mb-2">
              Meeting Link
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={shareLink}
                readOnly
                className="flex-1 px-4 py-3 bg-[#f5f5f7] dark:bg-[#2c2c2e] text-[#1d1d1f] dark:text-white rounded-xl border border-[#d2d2d7] dark:border-[#3c3c3e] font-mono text-sm truncate"
              />
              <button
                onClick={copyToClipboard}
                className={`px-4 py-3 rounded-xl font-medium transition ${
                  copied
                    ? 'bg-green-500 text-white'
                    : 'bg-[#06c] text-white hover:bg-[#0055d4]'
                }`}
              >
                {copied ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* Tab Selection */}
          <div className="flex gap-2 bg-[#f5f5f7] dark:bg-[#2c2c2e] p-1 rounded-xl">
            <button
              onClick={() => setActiveTab('contacts')}
              className={`flex-1 py-2 px-4 rounded-lg font-medium text-sm transition ${
                activeTab === 'contacts'
                  ? 'bg-white dark:bg-[#3c3c3e] text-[#1d1d1f] dark:text-white shadow'
                  : 'text-[#86868b] hover:text-[#1d1d1f] dark:hover:text-white'
              }`}
            >
              Contacts
            </button>
            <button
              onClick={() => setActiveTab('external')}
              className={`flex-1 py-2 px-4 rounded-lg font-medium text-sm transition ${
                activeTab === 'external'
                  ? 'bg-white dark:bg-[#3c3c3e] text-[#1d1d1f] dark:text-white shadow'
                  : 'text-[#86868b] hover:text-[#1d1d1f] dark:hover:text-white'
              }`}
            >
              External
            </button>
          </div>

          {/* Contacts Tab */}
          {activeTab === 'contacts' && (
            <div className="space-y-4">
              {/* Search Contacts */}
              <div className="relative">
                <svg
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#86868b]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search contacts"
                  className="w-full pl-10 pr-4 py-3 bg-[#f5f5f7] dark:bg-[#2c2c2e] text-[#1d1d1f] dark:text-white rounded-xl border border-[#d2d2d7] dark:border-[#3c3c3e] placeholder-[#86868b] focus:outline-none focus:ring-2 focus:ring-[#06c] focus:border-transparent transition-all"
                />
              </div>

              {/* Contact List */}
              <div className="max-h-48 overflow-y-auto space-y-2">
                {filteredContacts.length > 0 ? (
                  filteredContacts.map((contact) => {
                    const isSelected = selectedContacts.some(c => c.contactId === contact.contactId);
                    return (
                      <button
                        key={contact.contactId}
                        onClick={() => toggleContactSelection(contact)}
                        className={`w-full flex items-center gap-3 p-3 rounded-xl transition ${
                          isSelected
                            ? 'bg-[#06c]/10 dark:bg-[#06c]/20 ring-2 ring-[#06c]'
                            : 'bg-[#f5f5f7] dark:bg-[#2c2c2e] hover:bg-[#e8e8ed] dark:hover:bg-[#3c3c3e]'
                        }`}
                      >
                        {/* Avatar */}
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#06c] to-[#0055d4] flex items-center justify-center flex-shrink-0">
                          {contact.contactPhotoURL ? (
                            <img
                              src={contact.contactPhotoURL}
                              alt={contact.contactName}
                              className="w-full h-full rounded-full object-cover"
                            />
                          ) : (
                            <span className="text-white font-semibold">
                              {(contact.contactName || contact.contactEmail)?.[0]?.toUpperCase() || '?'}
                            </span>
                          )}
                        </div>

                        {/* Info */}
                        <div className="flex-1 text-left min-w-0">
                          <p className="font-medium text-[#1d1d1f] dark:text-white truncate">
                            {contact.nickname || contact.contactName || 'Unknown'}
                          </p>
                          {contact.contactEmail && (
                            <p className="text-sm text-[#86868b] truncate">
                              {contact.contactEmail}
                            </p>
                          )}
                        </div>

                        {/* Checkbox */}
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                          isSelected
                            ? 'bg-[#06c] text-white'
                            : 'border-2 border-[#d2d2d7] dark:border-[#3c3c3e]'
                        }`}>
                          {isSelected && (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                      </button>
                    );
                  })
                ) : (
                  <div className="text-center py-8">
                    <div className="w-12 h-12 bg-[#f5f5f7] dark:bg-[#2c2c2e] rounded-full flex items-center justify-center mx-auto mb-3">
                      <svg className="w-6 h-6 text-[#86868b]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <p className="text-[#86868b] text-sm">
                      {searchQuery ? 'No contacts found' : 'No contacts to share with'}
                    </p>
                  </div>
                )}
              </div>

              {/* Personal Note */}
              {selectedContacts.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-[#86868b] dark:text-[#8e8e93] mb-2">
                    Add a note (optional)
                  </label>
                  <textarea
                    value={personalNote}
                    onChange={(e) => setPersonalNote(e.target.value)}
                    placeholder="Add a personal message to your invite"
                    rows={2}
                    className="w-full px-4 py-3 bg-[#f5f5f7] dark:bg-[#2c2c2e] text-[#1d1d1f] dark:text-white rounded-xl border border-[#d2d2d7] dark:border-[#3c3c3e] placeholder-[#86868b] focus:outline-none focus:ring-2 focus:ring-[#06c] focus:border-transparent transition-all resize-none"
                  />
                </div>
              )}
            </div>
          )}

          {/* External Share Tab */}
          {activeTab === 'external' && (
            <div className="space-y-4">
              {/* Personal Note for External */}
              <div>
                <label className="block text-sm font-medium text-[#86868b] dark:text-[#8e8e93] mb-2">
                  Add a note (optional)
                </label>
                <textarea
                  value={personalNote}
                  onChange={(e) => setPersonalNote(e.target.value)}
                  placeholder="Add a personal message to your invite"
                  rows={2}
                  className="w-full px-4 py-3 bg-[#f5f5f7] dark:bg-[#2c2c2e] text-[#1d1d1f] dark:text-white rounded-xl border border-[#d2d2d7] dark:border-[#3c3c3e] placeholder-[#86868b] focus:outline-none focus:ring-2 focus:ring-[#06c] focus:border-transparent transition-all resize-none"
                />
              </div>

              {/* Share Buttons */}
              <div className="grid grid-cols-4 gap-3">
                <button
                  onClick={shareViaEmail}
                  className="flex flex-col items-center gap-2 p-4 bg-[#f5f5f7] dark:bg-[#2c2c2e] rounded-xl hover:bg-[#e8e8ed] dark:hover:bg-[#3c3c3e] transition"
                >
                  <svg className="w-6 h-6 text-[#06c]" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                  </svg>
                  <span className="text-xs font-medium text-[#1d1d1f] dark:text-white">Email</span>
                </button>

                <button
                  onClick={shareViaWhatsApp}
                  className="flex flex-col items-center gap-2 p-4 bg-[#f5f5f7] dark:bg-[#2c2c2e] rounded-xl hover:bg-[#e8e8ed] dark:hover:bg-[#3c3c3e] transition"
                >
                  <svg className="w-6 h-6 text-[#25D366]" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                  <span className="text-xs font-medium text-[#1d1d1f] dark:text-white">WhatsApp</span>
                </button>

                {navigator.share && (
                  <button
                    onClick={handleNativeShare}
                    className="flex flex-col items-center gap-2 p-4 bg-[#f5f5f7] dark:bg-[#2c2c2e] rounded-xl hover:bg-[#e8e8ed] dark:hover:bg-[#3c3c3e] transition"
                  >
                    <svg className="w-6 h-6 text-[#06c]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                    </svg>
                    <span className="text-xs font-medium text-[#1d1d1f] dark:text-white">More</span>
                  </button>
                )}

                <button
                  onClick={copyToClipboard}
                  className="flex flex-col items-center gap-2 p-4 bg-[#f5f5f7] dark:bg-[#2c2c2e] rounded-xl hover:bg-[#e8e8ed] dark:hover:bg-[#3c3c3e] transition"
                >
                  <svg className="w-6 h-6 text-[#06c]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  <span className="text-xs font-medium text-[#1d1d1f] dark:text-white">Copy</span>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {activeTab === 'contacts' && selectedContacts.length > 0 && (
          <div className="bg-[#f5f5f7] dark:bg-[#2c2c2e] border-t border-[#d2d2d7] dark:border-[#3c3c3e] px-6 py-4 flex justify-between items-center">
            <span className="text-sm text-[#86868b]">
              {selectedContacts.length} {selectedContacts.length === 1 ? 'contact' : 'contacts'} selected
            </span>
            <button
              onClick={sendInvitesToContacts}
              disabled={isSending}
              className="px-6 py-2.5 bg-[#06c] text-white rounded-xl font-medium hover:bg-[#0055d4] transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSending ? (
                <>
                  <svg className="w-4 h-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Sending...
                </>
              ) : (
                'Send Invite'
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
